/**
 * Untrusted HTML (an email body, already sanitized by the server) in a sandboxed frame: no scripts, no forms, no top
 * navigation, no referrer. Same-origin is allowed (with scripts off nothing can use it) so the frame can grow to its
 * content's height. The document gets a plain page style; in dark mode it is shown dark the way Outlook / Gmail do it,
 * by inverting the page's colours and turning images back. Links open in a new tab where the sandbox allows it.
 * iOS/Android: a non-persistent web view with JavaScript off.
 */
import { useEffect, useRef, useState } from "react";
import "./mailcal.css";

export type HtmlFrameProps = {
  html: string;
  /** The frame's accessible title (the message subject). */
  title: string;
  testID?: string;
};

const BASE =
  "<meta charset=\"utf-8\"><base target=\"_blank\"><style>html{color-scheme:light}body{margin:0;padding:4px 2px;font:14px/1.5 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#16161a;background:#fff;overflow-wrap:anywhere}img{max-width:100%;height:auto}blockquote{margin:0 0 0 .8ex;border-left:1px solid #999;padding-left:1ex}</style>";
/** Dark mode: the inverted page lands on the app's dark surface; images, video and background images turn back. */
const DARK =
  "<style>html{filter:invert(1) hue-rotate(180deg);background:#e6e6e6}body{background:transparent}img,video,picture,svg,[style*=\"background-image\"]{filter:invert(1) hue-rotate(180deg)}</style>";

/**
 * The reading view (body starting with `<!--bundle:readable-->`, Mux's approach): plain semantic HTML laid out as a
 * reading column in the app's own colours, light or dark, read from the page's tokens: no inverting.
 */
function readingCss(): string {
  const v = (name: string, fallback: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
  const text = v("--b-color-text-primary", "#16161a");
  const muted = v("--b-color-text-secondary", "#5e5e66");
  const bg = v("--b-color-bg-canvas", "#ffffff");
  const sunken = v("--b-color-bg-sunken", "#f4f4f6");
  const line = v("--b-color-border-subtle", "#e4e4e8");
  const link = v("--b-color-text-link", "#3b5bdb");
  return `<meta charset="utf-8"><base target="_blank"><style>
    html,body{margin:0;padding:0;background:${bg};color:${text}}
    body{padding:4px 2px;font:15px/1.65 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;-webkit-font-smoothing:antialiased;overflow-wrap:anywhere}
    h1,h2,h3,h4{line-height:1.3;margin:1.4em 0 .5em;font-weight:600}h1{font-size:1.45em}h2{font-size:1.25em}h3{font-size:1.1em}
    p{margin:0 0 1em}a{color:${link};text-underline-offset:3px}
    img{max-width:100%;height:auto;display:block;margin:1.2em auto;border-radius:8px}
    blockquote{margin:1.2em 0;padding:.1em 0 .1em 1em;border-left:3px solid ${line};color:${muted}}
    hr{border:0;border-top:1px solid ${line};margin:1.6em 0}ul,ol{padding-left:1.3em}li{margin:.3em 0}
    table{width:100%;border-collapse:collapse;margin:1.2em 0}th,td{border-bottom:1px solid ${line};padding:6px 8px;text-align:left}
    pre,code{font:13px/1.5 ui-monospace,SFMono-Regular,Menlo,monospace;background:${sunken};border-radius:6px;white-space:pre-wrap}code{padding:.1em .35em}pre{padding:10px}
  </style>`;
}

function frameDoc(html: string, dark: boolean): string {
  if (html.startsWith("<!--bundle:readable-->")) return `${readingCss()}${html}`;
  const head = dark ? `${BASE}${DARK}` : BASE;
  return /<head[^>]*>/i.test(html) ? html.replace(/<head[^>]*>/i, (h) => `${h}${head}`) : `${head}${html}`;
}

/** The app's theme: `data-theme` on the root, else the system's (tokens.css follows the same rule). */
function readDark(): boolean {
  const forced = document.documentElement.dataset.theme;
  return forced === "dark" || (forced !== "light" && window.matchMedia("(prefers-color-scheme: dark)").matches);
}

function useDark(): boolean {
  const [dark, setDark] = useState(readDark);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const update = () => setDark(readDark());
    mq.addEventListener("change", update);
    const mo = new MutationObserver(update);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => {
      mq.removeEventListener("change", update);
      mo.disconnect();
    };
  }, []);
  return dark;
}

export function HtmlFrame({ html, title, testID }: HtmlFrameProps) {
  const dark = useDark();
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);
  const stop = useRef<(() => void) | undefined>(undefined);
  useEffect(() => () => stop.current?.(), []);
  // As tall as its content (images loading later grow it too).
  const onLoad = () => {
    stop.current?.();
    const doc = ref.current?.contentDocument;
    if (doc === null || doc === undefined) return;
    const measure = () => setHeight(Math.ceil(doc.documentElement.scrollHeight) + 2);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(doc.body);
    // A reply's quote folded back behind "•••" (the server's <details class="bundle-quote">): the document is never
    // shorter than the frame, so the frame shrinks to the body instead (the iOS reader measures it the same way).
    const fold = (e: Event) => {
      const b = doc.body;
      const win = doc.defaultView;
      if ((e.target as HTMLDetailsElement | null)?.open !== false || b === null || win === null) return;
      const r = b.getBoundingClientRect();
      const h = Math.ceil(r.top + win.scrollY + Math.max(b.scrollHeight, r.height) + (Number.parseFloat(win.getComputedStyle(b).marginBottom) || 0)) + 2;
      if (ref.current !== null) ref.current.style.height = `${h}px`;
      setHeight(h);
      // Anything the body doesn't hold (a last child's margin) still fits: the document, at the new size, has the last word.
      requestAnimationFrame(measure);
    };
    doc.addEventListener("toggle", fold, true);
    // The frame is its own document: a click in it takes the keyboard and focuses nothing out here. Both go on from
    // the frame itself, so the app's shortcuts and pane focus work as if the message were part of the page.
    const key = (e: KeyboardEvent) => {
      const { key: k, code, repeat, shiftKey, metaKey, ctrlKey, altKey } = e;
      if (ref.current?.dispatchEvent(new KeyboardEvent("keydown", { key: k, code, repeat, shiftKey, metaKey, ctrlKey, altKey, bubbles: true, cancelable: true })) === false) e.preventDefault();
    };
    const press = () => ref.current?.dispatchEvent(new PointerEvent("pointerdown", { bubbles: true }));
    doc.addEventListener("keydown", key);
    doc.addEventListener("pointerdown", press);
    stop.current = () => {
      ro.disconnect();
      doc.removeEventListener("toggle", fold, true);
      doc.removeEventListener("keydown", key);
      doc.removeEventListener("pointerdown", press);
    };
  };
  return (
    <iframe
      ref={ref}
      className="b-htmlframe"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={frameDoc(html, dark)}
      title={title}
      referrerPolicy="no-referrer"
      onLoad={onLoad}
      style={height === undefined ? undefined : { height }}
      data-testid={testID}
    />
  );
}
